export const RELEASES_KEPT = 100;

export type Release = {
  release: string;
  timestamp: string;
  host: string;
  remotes: Record<string, string>;
};

export type Manifest = {
  current: string;
  releases: Release[];
};

export type ReleaseInput = {
  previous: Manifest;
  release: string;
  timestamp: string;
  version: string;
  builtRemotes: string[];
  hostBuilt: boolean;
};

export const emptyManifest = (): Manifest => ({ current: '', releases: [] });

export const byTimestamp = (releases: Release[]) =>
  [...releases].sort((one, other) => one.timestamp.localeCompare(other.timestamp));

export const latestRelease = (manifest: Manifest) => byTimestamp(manifest.releases).at(-1);

export const resolveRelease = (manifest: Manifest, hostVersion: string) => {
  const current = manifest.releases.find((release) => release.release === manifest.current);

  if (current?.host === hostVersion) {
    return current;
  }

  return byTimestamp(manifest.releases.filter((release) => release.host === hostVersion)).at(-1);
};

export const remoteEntryUrl = (remote: string, version: string) => `/${remote}/${version}/remoteEntry.js`;

export const buildManifest = ({
  previous,
  release,
  timestamp,
  version,
  builtRemotes,
  hostBuilt,
}: ReleaseInput): Manifest => {
  const latest = latestRelease(previous);

  if (!hostBuilt && !latest) {
    throw new Error('The host has never been published, so this release has no host version to record.');
  }

  const entry: Release = {
    release,
    timestamp,
    host: hostBuilt ? version : latest!.host,
    remotes: { ...latest?.remotes, ...Object.fromEntries(builtRemotes.map((remote) => [remote, version])) },
  };

  return {
    current: release,
    releases: byTimestamp([...previous.releases, entry]).slice(-RELEASES_KEPT),
  };
};
